/**
 * Validate the Convex client deployment URL used by ConvexReactClient.
 *
 * `.convex.cloud` is the client/WebSocket URL.
 * `.convex.site` is only for HTTP actions / webhooks — using it as
 * NEXT_PUBLIC_CONVEX_URL leaves the SPA unable to connect (often a blank dark page).
 *
 * A trailing slash is stripped: `https://….convex.cloud/` becomes
 * `wss://….convex.cloud//api/...` and the WebSocket handshake 404s, so queries
 * never resolve and the UI hangs on skeletons.
 */
export type ConvexUrlValidation =
    | { ok: true; url: string }
    | { ok: false; reason: "missing" | "http_site" | "invalid" };

/** Normalize a Convex deployment URL for ConvexReactClient (no trailing slash). */
export function normalizeConvexDeploymentUrl(raw: string): string {
    return raw.trim().replace(/\/+$/, "");
}

export function validateConvexDeploymentUrl(
    raw: string | undefined | null,
): ConvexUrlValidation {
    const trimmed = raw?.trim() ?? "";
    if (!trimmed) {
        return { ok: false, reason: "missing" };
    }

    let parsed: URL;
    try {
        parsed = new URL(trimmed);
    } catch {
        return { ok: false, reason: "invalid" };
    }

    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
        return { ok: false, reason: "invalid" };
    }

    const host = parsed.hostname.toLowerCase();
    if (host.endsWith(".convex.site")) {
        return { ok: false, reason: "http_site" };
    }

    return { ok: true, url: normalizeConvexDeploymentUrl(trimmed) };
}

export function suggestedCloudUrlFromSite(raw: string): string | null {
    try {
        const parsed = new URL(raw.trim());
        if (!parsed.hostname.toLowerCase().endsWith(".convex.site")) {
            return null;
        }
        parsed.hostname = parsed.hostname.replace(/\.convex\.site$/i, ".convex.cloud");
        parsed.pathname = "/";
        parsed.search = "";
        parsed.hash = "";
        return parsed.toString().replace(/\/$/, "");
    } catch {
        return null;
    }
}
